import { SelectedItems } from './doc-search/config';
import { cloneDeep } from 'lodash';
import React from 'react';
import { notify } from './utils/helper';

import * as api from './api';

enum DownloadStatus {
  ERROR = 'error',
  META_PENDING = 'meta-pending',
  META_SUCCESS = 'meta-success',
  PENDING = 'pending',
  SUCCESS = 'success',
}

export const defaultSearchFilters: SearchFilters = {
  form_status: new Set(['Active']) as FormStatus,
  is_expiring: false,
  form_type_of_business: new Set() as FormTypeOfBusiness,
  premium_bearing: 'No',
  has_fields: false,
  impact_on_coverage: new Set() as ImpactOnCoverage,
  form_usage_category: new Set() as FormUsageCategory,
  form_type: [],
  form_source: [],
  form_category: new Set() as FormCategory,
  carrier: [],
  hierarchy: [],
  line_of_business: [],
  state_filed: [],
  state_effective_date_min: null,
  state_effective_date_max: null,
  state_expired_date_min: null,
  state_expired_date_max: null,
};

const defaultFormSetsFilters: FormSetsFilters = {
  form_type: [],
  form_source: [],
  form_category: [],
  carrier: [],
  hierarchy: [],
  line_of_business: [],
  state_filed: [],
};

export const authDefault: Auth = {
  isAuthenticated: false,
  isAuthenticating: false,
  isTokenInvalid: false,
  didProcessRedirectToken: false,
  accessToken: '',
  provider: 'Okta',
  roles: [] as Array<string>,
  redirectUri: '',
  user: {
    name: '',
  },
};

const setupAuth = () => {
  try {
    const storedAuth = localStorage.getItem('paper-auth');
    if (storedAuth) {
      return JSON.parse(storedAuth);
    }
  } catch (error) {
    console.warn('Failed to parse auth object from localStorage', error);
  }

  return authDefault;
};

export const initialSearch: Search = {
  filters: cloneDeep(defaultSearchFilters),
  searchQuery: '',
  narrow: '',
  expandedIds: {},
  allExpanded: false,
};

const initialFormSets: FormSets = {
  error: undefined as unknown as string,
  hits: [] as Array<any>,
  hitsCount: 0,
  filters: cloneDeep(defaultFormSetsFilters),
  filtersAreDirty: false,
  wasRequested: false,
};

const initialViewDoc: ViewDoc = {
  hasError: false,
  id: '',
  fileUrl: '',
  canModifyFormNumber: false,
  metadata: {},
  downloadUrl: '',
  related: [],
  pdf: undefined as any,
  pdfViewer: undefined as any,
  formSource: undefined,
  status: undefined,
  user: { email: '' },
  errors: {
    products: false,
    states: [],
  },
  timestamp: '',
};

export const initialState: AppState = {
  isFetched: false,
  isLoading: false,
  auth: setupAuth(),
  download: {
    meta: [] as Array<any>,
    status: 'init' as FetchStatus,
  },
  hierarchyOptions: [] as Array<Option>,
  lobOptions: [] as Array<Option>,
  carrierOptions: [] as Array<Option>,
  search: cloneDeep(initialSearch),
  formSets: cloneDeep(initialFormSets),
  validation: {
    hasFinished: false,
    isPending: false,
    linesOfBusiness: [] as Array<string>,
    result: null as any,
  },
  viewDoc: cloneDeep(initialViewDoc),
  previousViewDocId: '',
  tempMetaData: {
    categoryType: '', // couldn't figure out what these two should be
    impactOnCoverage: '', // couldn't figure out what these two should be
    form_source: 'Proprietary',
    form_type: 'Endorsement',
    premium_bearing: '',
    raw_title: '',
    file_name_original: '',
    country: '',
    form_status: [],
    notes: '',
    file_name_pdf: '',
    form_usage_category: '',
    variable_static: '',
    bu: '',
    attachment: {},
    isMigratedFromFRRT: true,
    manual_rate_condition: '',
    raw_content: '',
    appian_id: '',
    form_type_of_business: [],
    form_title: '',
    line_of_business: '',
    opu: '',
    program_or_product: '',
    file_name_docx: '',
    form_number: '',
  },
};

// Actions
export const AUTH_REFRESH = 'AUTH_REFRESH';
export const AUTH_UPDATE = 'AUTH_UPDATE';
export const CLEAR_DOC_LIST = 'CLEAR_DOC_LIST';
export const CLEAR_EXPORT_FILTERS = 'CLEAR_EXPORT_FILTERS';
export const CLEAR_SEARCH_FILTERS = 'CLEAR_SEARCH_FILTERS';
export const CLEAR_SEARCH_HITS = 'CLEAR_SEARCH_HITS';
export const CLEAR_USER = 'CLEAR_USER';
export const HANDLE_API_ERROR = 'HANDLE_API_ERROR';
export const RESET_STATE = 'RESET_STATE';
export const SET_DOCUMENTS_META = 'SET_DOCUMENTS_META';
export const SET_DOWNLOAD_STATUS = 'SET_DOWNLOAD_STATUS';
export const SET_FORM_SETS_DATE_FILTER = 'SET_FORM_SETS_DATE_FILTER';
export const SET_FORM_SETS_SELECT_FILTER = 'SET_FORM_SETS_SELECT_FILTER';
export const SET_IS_LOADING = 'SET_IS_LOADING';
export const SET_ORG_HIERARCHY = 'SET_ORG_HIERARCHY';
export const SET_USER = 'SET_USER';
export const UPDATE_SEARCH_FILTER = 'UPDATE_SEARCH_FILTER';
export const UPDATE_DOC_LIST = 'UPDATE_DOC_LIST';
export const UPDATE_DOCUMENTS = 'UPDATE_DOCUMENTS';
export const UPDATE_FORMSETS = 'UPDATE_FORMSETS';
export const UPDATE_MERGED_DOCS_FOR_PREVIEW = 'UPDATE_MERGED_DOCS_FOR_PREVIEW';
export const UPDATE_META_DATA_LIST = 'UPDATE_META_DATA_LIST';
export const UPDATE_SEARCH_QUERY = 'UPDATE_SEARCH_QUERY';
export const UPDATE_SEARCH_SCOPE = 'UPDATE_SEARCH_SCOPE';
export const UPDATE_SEARCH_RESULTS = 'UPDATE_SEARCH_RESULTS';
export const UPDATE_FORM_STATE_AND_SOURCE = 'UPDATE_FORM_STATE_AND_SOURCE';
export const RESET_VIEW_DOC = 'RESET_VIEW_DOC';
export const UPDATE_VIEW_DOC_USER = 'UPDATE_VIEW_DOC_USER';
export const UPDATE_ERRORS = 'UPDATE_ERRORS';
export const SET_PREVIOUS_VIEWDOC_ID = 'SET_PREVIOUS_VIEWDOC_ID';
export const SET_FILTERS_FROM_SAVED = 'SET_FILTERS_FROM_SAVED';
export const SHOW_RESULT_CARD_DETAILS = 'SHOW_RESULT_CARD_DETAILS';
export const SHOW_ALL_RESULT_CARD_DETAILS = 'SHOW_ALL_RESULT_CARD_DETAILS';

// Action Creators
export const clearDocList = () => {
  const auth = {
    search: cloneDeep(initialSearch),
    isLoading: false,
    isFetched: false,
  };

  return { type: CLEAR_DOC_LIST, payload: auth };
};

export const clearExportFilters = () => {
  const formSets = {
    filters: cloneDeep(defaultFormSetsFilters),
    filtersAreDirty: false,
    wasRequested: false,
  };

  return { type: CLEAR_EXPORT_FILTERS, payload: formSets };
};

export const clearSearchFilters = (state: AppState) => {
  const search = { ...state.search };

  search.filters = cloneDeep(defaultSearchFilters);

  return { type: CLEAR_SEARCH_FILTERS, payload: search };
};

export const clearSearchHits = () => ({ type: CLEAR_SEARCH_HITS });

export const clearUser = (): ActionCreator => {
  const auth = {
    isAuthenticated: false,
    provider: '',
    accessToken: '',
    user: { name: '' },
    isTokenInvalid: false,
    didProcessRedirectToken: false,
    redirectUri: '',
  };
  localStorage.removeItem('paper-auth');
  return { type: CLEAR_USER, payload: auth };
};

export const resetState = () => ({ type: RESET_STATE });

export const getDocumentsMeta = async (formNumbers: string, dispatch: React.Dispatch<any>) => {
  if (/\|/.test(formNumbers)) {
    notify(
      'Form numbers must be separated by double tilde (~~) instead of a vertical bar (|)',
      'error'
    );
    dispatch(setDownloadStatus(DownloadStatus.ERROR));
    return;
  }
  if (/[^~]~[^~]/.test(formNumbers)) {
    notify('Form number separator must be a double tilde instead of a single', 'warn');
  }
  dispatch(setDownloadStatus(DownloadStatus.META_PENDING));
  const res = await api.getDocumentsMeta(formNumbers);
  if (res === false) {
    dispatch(setDownloadStatus(DownloadStatus.ERROR));
  } else {
    dispatch(setDownloadStatus(DownloadStatus.META_SUCCESS));

    return { type: SET_DOCUMENTS_META, payload: res };
  }
};

export const setDownloadStatus = (status: string) => ({
  type: SET_DOWNLOAD_STATUS,
  payload: status,
});

export const setFormSetsDateFilter = (filterId: FormSetsFilterId, date: Date) => {
  return {
    type: SET_FORM_SETS_DATE_FILTER,
    payload: { filtersAreDirty: true, filters: { [filterId]: date } },
  };
};

export const setFormSetsSelectFilter = (filterId: FormSetsFilterId, items: SelectedItems) => {
  return {
    type: SET_FORM_SETS_SELECT_FILTER,
    payload: { [filterId]: items },
  };
};

export const updateFormSets = (formSets: FormSets) => {
  return {
    type: UPDATE_FORMSETS,
    payload: formSets,
  };
};

export const setIsLoading = (isLoading: boolean) => ({ type: SET_IS_LOADING, payload: isLoading });

export const updateOrgHierarchy = (payload: any) => ({
  type: SET_ORG_HIERARCHY,
  payload,
});

export const setSelectFilter = (
  filterId: SearchFilterId,
  items: SelectedItems,
  filters: SearchFilters
) => {
  const newFilters = cloneDeep(filters);
  if (newFilters[filterId]) {
    newFilters[filterId] = items || [];
  }

  return {
    type: UPDATE_SEARCH_FILTER,
    payload: newFilters,
  };
};

export const setRadioAndDatePickerFilter = (
  newValue: any,
  filterId: SearchFilterId,
  filters: SearchFilters
) => {
  const newFilters = cloneDeep(filters);
  if (newFilters[filterId] !== undefined) {
    newFilters[filterId] = newValue;
  }

  return {
    type: UPDATE_SEARCH_FILTER,
    payload: newFilters,
  };
};

export const setCheckboxFilter = (
  filterId: SearchFilterId,
  option: string,
  checked: boolean,
  filters: SearchFilters
) => {
  const newFilters = cloneDeep(filters);
  if (
    ![
      'impact_on_coverage',
      'form_usage_category',
      'form_status',
      'form_type_of_business',
      'form_category',
    ].includes(filterId)
  )
    return;
  if (checked) {
    (newFilters[filterId] as Set<any>).add(option);
  } else {
    (newFilters[filterId] as Set<any>).delete(option);
  }
  return { type: UPDATE_SEARCH_FILTER, payload: newFilters };
};

export const setUser = (
  name: string,
  accessToken: string,
  provider: 'Okta',
  roles?: Array<string>,
  oktaEmail?: string,
  oktaUsername?: string
) => {
  const email = oktaEmail !== oktaUsername ? oktaUsername : oktaEmail;

  const auth = {
    provider: provider,
    isAuthenticating: false,
    isAuthenticated: true,
    accessToken: accessToken,
    user: { name, email },
    isTokenInvalid: false,
    roles: roles || [],
    didProcessRedirectToken: false,
    redirectUri: '',
  };
  localStorage.setItem('paper-auth', JSON.stringify(auth));
  return { type: SET_USER, payload: auth };
};

export const refreshAuth = () => ({ type: AUTH_REFRESH });

export const updateAuth = (auth: Auth) => {
  localStorage.setItem('paper-auth', JSON.stringify(auth));
  return { type: AUTH_UPDATE, payload: auth };
};

export const setSearchQuery = (query: string) => ({ type: UPDATE_SEARCH_QUERY, payload: query });

export const setSearchScope = (narrow?: string) => ({ type: UPDATE_SEARCH_SCOPE, payload: narrow });

export const validateDocuments = async (query: Array<ValidationQuery>, state: AppState) => {
  const validation = { ...state.validation };

  if (validation.isPending || validation.hasFinished) {
    return;
  }

  validation.isPending = true;
  validation.linesOfBusiness = query.map((q) => q.l);
  validation.result = await api.getValidation(query);
  validation.isPending = false;
  validation.hasFinished = true;

  return { type: UPDATE_DOCUMENTS, payload: validation };
};

export const setViewDoc = (viewDoc: ViewDoc) => ({
  type: UPDATE_MERGED_DOCS_FOR_PREVIEW,
  payload: viewDoc,
});

export const getMergedDocumentsFile = async (formNumbers: string) => {
  if (/\|/.test(formNumbers)) {
    notify(
      'Form numbers must be separated by double tilde (~~) instead of a vertical bar (|)',
      'error'
    );
    return;
  }

  await api.getMergedDocumentsFile(formNumbers);
};

export const getMergedDocumentsZip = async (formNumbers: string, dispatch: React.Dispatch<any>) => {
  if (/\|/.test(formNumbers)) {
    notify(
      'Form numbers must be separated by double tilde (~~) instead of a vertical bar (|)',
      'error'
    );
    return;
  }

  dispatch(setDownloadStatus(DownloadStatus.PENDING));

  const res = await api.getMergedDocumentsZip(formNumbers);

  if (res === true) {
    return setDownloadStatus(DownloadStatus.SUCCESS);
  }
  if (res === false) {
    return setDownloadStatus(DownloadStatus.ERROR);
  }
};

// Reducer
export const reducer = (state: AppState, action: ActionCreator) => {
  switch (action.type) {
    case AUTH_REFRESH:
      return {
        ...state,
        auth: { ...state.auth, isTokenInvalid: true, didProcessRedirectToken: true },
      };
    case AUTH_UPDATE:
      return { ...state, auth: { ...state.auth, ...action.payload } };
    case CLEAR_DOC_LIST:
      return { ...state, auth: action.payload };
    case CLEAR_EXPORT_FILTERS:
      return {
        ...state,
        formSets: { ...state.formSets, ...action.payload, hits: [], hitsCount: 0 },
      };
    case CLEAR_SEARCH_FILTERS:
      return { ...state, search: action.payload };
    case CLEAR_SEARCH_HITS:
      return { ...state, search: { ...state.search, hits: [], hitsCount: 0, totalHits: 0 } };
    case CLEAR_USER:
      return { ...state, auth: action.payload };
    case RESET_STATE:
      return { ...initialState };
    case SET_DOCUMENTS_META:
      return { ...state, download: { ...state.download, meta: action.payload } };
    case SET_DOWNLOAD_STATUS:
      return { ...state, download: { ...state.download, status: action.payload } };
    case SET_FORM_SETS_DATE_FILTER:
      return {
        ...state,
        formSets: {
          ...state.formSets,
          filtersAreDirty: action.payload.filtersAreDirty,
          filters: {
            ...state.formSets.filters,
            ...action.payload.filters,
          },
        },
      };
    case SET_FORM_SETS_SELECT_FILTER:
      return {
        ...state,
        formSets: {
          ...state.formSets,
          filtersAreDirty: true,
          filters: { ...state.formSets.filters, ...action.payload },
        },
      };
    case SET_IS_LOADING:
      return { ...state, isLoading: action.payload };
    case SET_ORG_HIERARCHY:
      return { ...state, ...action.payload };
    case SET_USER:
      return { ...state, auth: action.payload };
    case UPDATE_FORMSETS:
      return { ...state, formSets: { ...state.formSets, ...action.payload } };
    case UPDATE_SEARCH_FILTER:
      return { ...state, search: { ...state.search, filters: action.payload } };
    case UPDATE_DOC_LIST:
      return {
        ...state,
        search: {
          ...state.search,
          ...action.payload.search,
        },
        viewDoc: {
          ...state.viewDoc,
          ...action.payload.viewDoc,
        },
        isFetched: action.payload.isFetched,
      };
    case UPDATE_DOCUMENTS:
      return { ...state, validation: action.payload };
    case UPDATE_MERGED_DOCS_FOR_PREVIEW:
      return { ...state, viewDoc: action.payload };
    case UPDATE_META_DATA_LIST: {
      const { isLoading, formSets, isFetched } = action.payload;

      return { ...state, isLoading, isFetched, formSets: { ...state.formSets, ...formSets } };
    }
    case UPDATE_SEARCH_QUERY:
      return { ...state, search: { ...state.search, searchQuery: action.payload } };
    case UPDATE_SEARCH_SCOPE:
      return { ...state, search: { ...state.search, narrow: action.payload } };
    case UPDATE_SEARCH_RESULTS:
      return { ...state, search: { ...state.search, hits: action.payload } };
    case UPDATE_FORM_STATE_AND_SOURCE:
      return {
        ...state,
        viewDoc: {
          ...state.viewDoc,
          ...{ status: action.payload.status, formSource: action.payload.formSource },
        },
      };
    case RESET_VIEW_DOC:
      return { ...state, viewDoc: initialViewDoc };
    case UPDATE_VIEW_DOC_USER:
      return { ...state, viewDoc: { ...state.viewDoc, user: action.payload } };
    case UPDATE_ERRORS:
      return { ...state, viewDoc: { ...state.viewDoc, errors: action.payload } };
    case SET_PREVIOUS_VIEWDOC_ID:
      return { ...state, previousViewDocId: action.payload };
    case SET_FILTERS_FROM_SAVED:
      return {
        ...state,
        search: { ...state.search, filters: action.payload },
      };
    case SHOW_RESULT_CARD_DETAILS: {
      const { id } = action.payload;
      const newExpandedIds = cloneDeep(state.search.expandedIds);

      if (newExpandedIds[id]) newExpandedIds[id] = false;
      else newExpandedIds[id] = true;

      return {
        ...state,
        search: {
          ...state.search,
          expandedIds: newExpandedIds,
        },
      };
    }

    case SHOW_ALL_RESULT_CARD_DETAILS: {
      const { ids, allExpanded } = action.payload;
      const newExpandedIds = ids?.reduce(
        (obj: Record<string, boolean>, id: string) => ({ ...obj, [id]: allExpanded }),
        {}
      );

      return {
        ...state,
        search: {
          ...state.search,
          expandedIds: newExpandedIds,
          allExpanded,
        },
      };
    }
    default:
      return state;
  }
};
